import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface Clip {
  id: string;
  title: string;
  source: string;
  source_url: string | null;
  duration_seconds: number;
  is_active: boolean;
  battle_count: number;
  created_at: string;
}

export default function ClipManager() {
  const { authToken } = useAuth();
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const fetchClips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/admin/clips`, { headers });
      if (!res.ok) throw new Error(`Failed to fetch clips: ${res.status}`);
      const data = await res.json();
      setClips(data.clips || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clips');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  const toggleActive = async (clip: Clip) => {
    setTogglingId(clip.id);
    try {
      const res = await fetch(`${API_BASE}/admin/clips/${clip.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_active: !clip.is_active }),
      });
      if (!res.ok) throw new Error(`Failed to update clip: ${res.status}`);
      setClips((prev) =>
        prev.map((c) =>
          c.id === clip.id ? { ...c, is_active: !c.is_active } : c
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle clip');
    } finally {
      setTogglingId(null);
    }
  };

  const truncateId = (id: string) => {
    if (id.length <= 12) return id;
    return `${id.slice(0, 6)}...${id.slice(-4)}`;
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-arena-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-arena-muted">Loading clips...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchClips} className="ml-3 underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-arena-muted">
          {clips.length} clip{clips.length !== 1 ? 's' : ''} total
        </p>
        <button onClick={fetchClips} className="btn btn-secondary text-sm">
          Refresh
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-arena-border text-arena-muted text-sm">
                <th className="text-left p-3 font-medium">ID</th>
                <th className="text-left p-3 font-medium">Title</th>
                <th className="text-left p-3 font-medium">Source</th>
                <th className="text-right p-3 font-medium">Duration</th>
                <th className="text-center p-3 font-medium">Active</th>
                <th className="text-right p-3 font-medium">Battles</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clips.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-arena-muted">
                    No clips found.
                  </td>
                </tr>
              ) : (
                clips.map((clip) => (
                  <tr
                    key={clip.id}
                    className="border-b border-arena-border last:border-0 hover:bg-arena-surface/50 transition-colors"
                  >
                    <td className="p-3">
                      <span className="font-mono text-xs text-arena-muted" title={clip.id}>
                        {truncateId(clip.id)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-arena-text">{clip.title}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-arena-muted">{clip.source}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-mono text-sm">{formatDuration(clip.duration_seconds)}</span>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => toggleActive(clip)}
                        disabled={togglingId === clip.id}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          clip.is_active ? 'bg-arena-accent' : 'bg-arena-border'
                        } ${togglingId === clip.id ? 'opacity-50' : ''}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                            clip.is_active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-mono text-sm">{clip.battle_count}</span>
                    </td>
                    <td className="p-3 text-right">
                      {clip.source_url && (
                        <a
                          href={clip.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-arena-accent hover:underline text-sm"
                        >
                          View Source
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
